import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/data-table';
import { useQuery } from '@tanstack/react-query';
import { fetchMembers } from '@/services/workspace';
import { legacyCreateColumnHelper } from '@tanstack/react-table/legacy';
import { InviteMemberDialog } from '@/routes/console/_authenticated/_layout/workspace/_layout/-features/invite-member-dialog';
import { InviteMemberLocked } from '@/routes/console/_authenticated/_layout/workspace/_layout/-features/invite-member-locked';
import { isRoleAbove, isRoleAtLeast } from '@/lib/role';
import { MemberActions } from '@/routes/console/_authenticated/_layout/workspace/_layout/-features/member-actions';
const columnHelper = legacyCreateColumnHelper();
export const MembersDataTable = ({ user, workspace }) => {
    const { t } = useTranslation();
    const [rowSelection, setRowSelection] = React.useState({});
    const { data: members = [] } = useQuery({
        queryKey: ['workspace-members', workspace.publicId],
        queryFn: () => fetchMembers(workspace.publicId),
    });
    const columns = React.useMemo(() => [
        columnHelper.accessor('email', {
            header: () => t('email'),
            cell: (info) => info.getValue(),
            footer: () => t('email'),
        }),
        columnHelper.accessor('fullName', {
            header: () => t('full_name'),
            cell: (info) => info.getValue(),
            footer: () => t('full_name'),
        }),
        columnHelper.accessor('role', {
            header: () => t('role'),
            cell: (info) => t(info.getValue().toLowerCase()),
            footer: () => t('role'),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => t('actions'),
            cell: ({ row }) => {
                const member = row.original;
                const currentRole = workspace.role;
                const targetRole = member.role;
                const isSelf = member.publicId === user.publicId;
                const canManage = !isSelf && targetRole !== 'OWNER' && isRoleAbove(currentRole, targetRole);
                if (!canManage) {
                    return null;
                }
                return <MemberActions member={member} workspace={workspace}/>;
            },
            footer: () => t('actions'),
        }),
    ], [t, user.publicId, workspace]);
    /**
     * Three distinct reasons the invite button is disabled — each gets its
     * own tooltip copy so a Team-plan admin at their seat cap doesn't see
     * "Upgrade to Pro" (they already are on a paid tier).
     *   - plan_locked  : plan doesn't include team invites at all
     *                    (max <= 1 effectively prevents inviting anyone)
     *   - plan_limit   : plan supports invites but seats are full
     *   - role_too_low : seats fine, current user can't issue invites
     *   - null         : enabled
     */
    const lockReason = React.useMemo(() => {
        const maxTeamMembers = workspace.subscription.plan.maxTeamMembers;
        // Treat null as unlimited.
        const seatsAvailable = maxTeamMembers === null ? true : maxTeamMembers > members.length;
        const planSupportsInvites = maxTeamMembers === null || maxTeamMembers > 1;
        if (!isRoleAtLeast(workspace.role, 'ADMIN'))
            return 'role_too_low';
        if (!planSupportsInvites)
            return 'plan_locked';
        if (!seatsAvailable)
            return 'plan_limit';
        return null;
    }, [members.length, workspace.role, workspace.subscription.plan.maxTeamMembers]);
    const canInvite = lockReason === null;
    return (<Card className="w-full">
            <CardHeader className="flex justify-between items-center">
                <CardTitle className="text-base md:text-lg">
                    {t('workspaces.members.title', { ns: 'glossary' })}
                </CardTitle>
                {canInvite ? (<InviteMemberDialog workspace={workspace} enabled/>) : (<InviteMemberLocked reason={lockReason}/>)}
            </CardHeader>
            <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">{t('workspaces.members.help', { ns: 'glossary' })}</p>
                <DataTable data={members} columns={columns} rowSelection={rowSelection} onRowSelectionChange={setRowSelection}/>
            </CardContent>
        </Card>);
};
